import React, { useState, useEffect, useRef, useCallback } from 'react';
import { attachFormEnterToNextField } from '../../utils/formEnterFocusNext';
import Form, {
    Label, SimpleItem, GroupItem, TabbedItem, TabPanelOptions, Tab
} from '../../components/BootstrapForm';
import { Popup, ScrollView } from '../../components/BootstrapControls';
import Axios from '../../api/axios';
import notify from '../../utils/notifyUtil';
import query from 'devextreme/data/query';
import Dict from '../../messages.js';
import * as glb from '../../globals.js';
import LoadingElapsed from '../../components/LoadingElapsed.js';
import { addMaxLengthToEditorOptions } from '../../utils/formValidationHelper.js';

export default function AnemnezGirPanel({ visible, carRef, carAdSoy, onHiding }) {
    const [formData, setFormData] = useState({});
    const [fullData, setFullData] = useState({});  // Tüm cari verisi
    const [loading, setLoading] = useState(false);
    const formRef = useRef(null);
    const hasLoadedRef = useRef(false);
    const isMountedRef = useRef(true);
    const enterNavCleanupRef = useRef(null);
    const attachEnterNavRoot = useCallback((node) => {
        if (enterNavCleanupRef.current) {
            enterNavCleanupRef.current();
            enterNavCleanupRef.current = null;
        }
        if (node) {
            enterNavCleanupRef.current = attachFormEnterToNextField(node);
        }
    }, []);
    const screen = query(glb.default.EKRANLkp).filter(x => x.EKR_EKRANADI === 'AnemnezGirPanel').toArray()[0];

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (enterNavCleanupRef.current) {
                enterNavCleanupRef.current();
                enterNavCleanupRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (visible && carRef && !hasLoadedRef.current) {
            hasLoadedRef.current = true;
            fetchAnemnez();
        }
        if (!visible && hasLoadedRef.current) {
            hasLoadedRef.current = false;
            setFormData({});
            setFullData({});
        }
    }, [visible, carRef]);

    const fetchAnemnez = useCallback(async () => {
        if (!carRef) return;
        setLoading(true);
        try {
            const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_KART/rec`, {
                params: {
                    pnm: screen.EKR_TABLOADI,
                    CAR_REF: carRef,
                    ap: 'update',
                    cid: glb.default.KULLANICI.KUL_DEF_FIR_REF
                }
            });
            if (isMountedRef.current && res.data?.carikart) {
                const data = res.data.carikart;
                setFullData(data);
                // Sadece anemnez alanlarını formData'ya al
                const anemnezFields = [
                    'CAR_REF', 'CAR_KRON_HAS', 'CAR_KUL_ILAC', 'CAR_AML_OPS', 'CAR_OZ_GECMIS',
                    'CAR_SAC_DER_HAS', 'CAR_SAC_ANALIZ', 'CAR_DONCE_SAC_EKIM_VAR', 'CAR_ESKISACZMN',
                    'CAR_KISI_UYAR', 'CAR_ACK', 'CAR_DOSYA_OK', 'CAR_NOT',
                    'CAR_HAMILE_RISK', 'CAR_EMZIRIYOR', 'CAR_SEKER_HST', 'CAR_SIS_ENFK',
                    'CAR_UYG_KOZM', 'CAR_UYG_ALN_ENFK', 'CAR_ILAC_KUL', 'CAR_KAN_YATKN',
                    'CAR_ASP_KANSU_KUL', 'CAR_HBS_HCV',
                    'DT01', 'DT02', 'DT03', 'DT04', 'DT05', 'DT06', 'DT07', 'DT08',
                    'DT09', 'DT10', 'DT11', 'DT12', 'DT13', 'DT14', 'DT15',
                    'CAR_ARA_KUL_REF'
                ];
                const filteredData = {};
                anemnezFields.forEach(field => {
                    if (field in data) filteredData[field] = data[field];
                });
                filteredData.CAR_ARA_KUL_REF = glb.default.KULLANICI.KUL_REF; // Yetki için
                setFormData(filteredData);
            } else {
                notify(Dict.get('anyItemNotFound'), 'warning', 1000);
            }
        } catch (err) {
            if (isMountedRef.current) {
                notify(Dict.get('error') + ': ' + (err.response?.data?.message || err.message), 'error', 3000);
            }
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [carRef, screen]);

    const handleSave = useCallback(async () => {
        if (!formRef.current) return;
        const validation = formRef.current.instance.validate();
        if (!validation.isValid) {
            notify(Dict.get('Lütfen gerekli alanları doldurun'), 'warning', 2000);
            return;
        }

        const currentFormData = formRef.current.instance.option('formData');
        const sendData = {
            data: currentFormData,
            type: 'update',
            pnm: screen.EKR_TABLOADI,
            uid: glb.default.KULLANICI.KUL_REF,
            vid: 1,
            cid: glb.default.KULLANICI.KUL_FIR_REF,
            defcid: glb.default.KULLANICI.KUL_DEF_FIR_REF,
            isource: 1,
            lang: glb.default.KULLANICI.KUL_DILKOD,
            inip: '??'
        };

        setLoading(true);
        try {
            const res = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_KART/anemnezkaydet`, sendData);
            if (isMountedRef.current) {
                if (res.data?.status === 'success' || res.data?.message === 'ok') {
                    notify(Dict.get('saved'), 'success', 1000);
                    onHiding();
                } else {
                    notify(Dict.get('error') + ': ' + (res.data?.message || 'Unknown error'), 'error', 3000);
                }
            }
        } catch (err) {
            if (isMountedRef.current) {
                notify(Dict.get('error') + ': ' + (err.response?.data?.message || err.message), 'error', 3000);
            }
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [screen, onHiding]);

    const handleCancel = useCallback(() => {
        onHiding();
    }, [onHiding]);

    return (
        <Popup
            visible={visible}
            onHiding={onHiding}
            dragEnabled={false}
            hideOnOutsideClick={false}
            showCloseButton={false}
            width="100%"
            height="100%"
            toolbarItems={[
                {
                    location: 'center',
                    widget: 'dxTextBox',
                    options: {
                        text: `${Dict.get('Anemnez / Sağlık Bilgileri')} (${carRef || ''}${carAdSoy ? ' - ' + carAdSoy : ''})`,
                        readOnly: true,
                        width: 480,
                        height: 30,
                        textAlign: 'center'
                    }
                },
                {
                    location: 'after',
                    widget: 'dxButton',
                    options: {
                        text: Dict.get('Kaydet'),
                        type: 'success',
                        icon: 'save',
                        onClick: handleSave,
                        disabled: loading
                    }
                },
                {
                    location: 'after',
                    widget: 'dxButton',
                    options: {
                        text: Dict.get('Vazgeç'),
                        type: 'default',
                        icon: 'close',
                        onClick: handleCancel
                    }
                }
            ]}
        >
            <div ref={attachEnterNavRoot} style={{ width: '100%', height: '100%' }}>
            {loading && <div className="alert alert-info m-2 py-2"><LoadingElapsed text="Anemnez yükleniyor" /></div>}
            <ScrollView width="100%" height="100%">
                <Form ref={formRef} formData={formData} labelLocation="left" showColonAfterLabel={false} disabled={loading}>
                    <GroupItem>
                        <SimpleItem render={() => (
                            <div style={{
                                padding: '10px 15px',
                                background: 'var(--dx-color-bg-secondary, #f8f9fa)',
                                borderBottom: '2px solid var(--dx-color-border, #dee2e6)',
                                margin: '10px 0',
                                borderRadius: '4px 4px 0 0'
                            }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: 'var(--dx-color-text, #212529)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <span style={{ fontSize: '18px' }}>🏥</span>
                                    {Dict.get('Anemnez / Sağlık Bilgileri')}
                                </h3>
                            </div>
                        )} />
                        <GroupItem>
                            <SimpleItem dataField='CAR_KRON_HAS' editorType='dxTextArea'
                                label={{ text: Dict.get('Kronik Hast.') }}
                                editorOptions={addMaxLengthToEditorOptions('CARI_KART', 'CAR_KRON_HAS', {
                                    minHeight: '20px', 
                                    autoResizeEnabled: process.env.NODE_ENV === 'production'
                                })} />
                            <SimpleItem dataField='CAR_KUL_ILAC' editorType='dxTextArea'
                                label={{ text: Dict.get('Kullan.İlaç') }}
                                editorOptions={addMaxLengthToEditorOptions('CARI_KART', 'CAR_KUL_ILAC', {
                                    minHeight: '20px', 
                                    autoResizeEnabled: process.env.NODE_ENV === 'production'
                                })} />
                        </GroupItem>
                        <GroupItem>
                            <SimpleItem dataField='CAR_AML_OPS' editorType='dxTextArea'
                                label={{ text: Dict.get('Ameliyat/Geç.Ops.') }}
                                editorOptions={addMaxLengthToEditorOptions('CARI_KART', 'CAR_AML_OPS', {
                                    minHeight: '20px', 
                                    autoResizeEnabled: process.env.NODE_ENV === 'production'
                                })} />
                            <SimpleItem dataField='CAR_OZ_GECMIS' editorType='dxTextArea'
                                label={{ text: Dict.get('Sgr/Alkol/Madde') }}
                                editorOptions={addMaxLengthToEditorOptions('CARI_KART', 'CAR_OZ_GECMIS', {
                                    minHeight: '20px', 
                                    autoResizeEnabled: process.env.NODE_ENV === 'production'
                                })} />
                        </GroupItem>
                        <GroupItem>
                            <SimpleItem dataField='CAR_SAC_DER_HAS' editorType='dxTextArea'
                                label={{ text: Dict.get('Saç Deri.Hast.') }}
                                editorOptions={addMaxLengthToEditorOptions('CARI_KART', 'CAR_SAC_DER_HAS', {
                                    minHeight: '20px', 
                                    autoResizeEnabled: process.env.NODE_ENV === 'production'
                                })} />
                            <SimpleItem dataField='CAR_SAC_ANALIZ' editorType='dxTextArea'
                                label={{ text: Dict.get('Saç Analiz') }}
                                editorOptions={addMaxLengthToEditorOptions('CARI_KART', 'CAR_SAC_ANALIZ', {
                                    minHeight: '20px', 
                                    autoResizeEnabled: process.env.NODE_ENV === 'production'
                                })} />
                        </GroupItem>
                      <GroupItem colCount={12}>
                                                              <SimpleItem colSpan={2} dataField='CAR_DONCE_SAC_EKIM_VAR' editorType='dxCheckBox'>
                                                                  <Label text={Dict.get('Eski Saç Ekim Var?')} />
                                                              </SimpleItem>
                                                              <SimpleItem
                                                                  colSpan={6}
                                                                  dataField='CAR_ESKISACZMN'
                                                                  editorType='dxTextBox'
                                                                  editorOptions={{ maxLength: 50 }}
                                                              >
                                                                  <Label text={Dict.get('Açıklama')} />
                                                              </SimpleItem>
                                                          </GroupItem>
                                                          <GroupItem colCount={12}>
                                                              <SimpleItem colSpan={2} dataField='CAR_KISI_UYAR' editorType='dxCheckBox'>
                                                                  <Label text={Dict.get('UYAR')} />
                                                              </SimpleItem>
                                                              <SimpleItem colSpan={10} dataField='CAR_ACK' editorType='dxTextArea' label={{ visible: false }}
                                                                  editorOptions={addMaxLengthToEditorOptions('CARI_KART', 'CAR_ACK', {
                                                                      minHeight: '20px', 
                                                                      autoResizeEnabled: process.env.NODE_ENV === 'production'
                                                                  })} />
                                                          </GroupItem>
                                                          <GroupItem colCount={12}>
                                                              <SimpleItem colSpan={2} dataField='CAR_DOSYA_OK' editorType='dxCheckBox'>
                                                                  <Label text={Dict.get('Yetkili Açklama')} />
                                                              </SimpleItem>
                                                              <SimpleItem colSpan={10} dataField='CAR_NOT' editorType='dxTextArea' label={{ visible: false }}
                                                                  editorOptions={addMaxLengthToEditorOptions('CARI_KART', 'CAR_NOT', {
                                                                      minHeight: '20px', 
                                                                      autoResizeEnabled: process.env.NODE_ENV === 'production'
                                                                  })} />
                                                          </GroupItem>
                        <TabbedItem>
                            <TabPanelOptions deferRendering={false} />
                            <Tab title={Dict.get('Sağlık Detay')}>
                                <GroupItem colCount={2}>
                                    <SimpleItem dataField='CAR_HAMILE_RISK' editorType='dxCheckBox'><Label text={Dict.get('Hamile.Risk')} /></SimpleItem>
                                    <SimpleItem dataField='CAR_EMZIRIYOR' editorType='dxCheckBox'><Label text={Dict.get('Emziriyor')} /></SimpleItem>
                                    <SimpleItem dataField='CAR_SEKER_HST' editorType='dxCheckBox'><Label text={Dict.get('Şekr.Hst.')} /></SimpleItem>
                                    <SimpleItem dataField='CAR_SIS_ENFK' editorType='dxCheckBox'><Label text={Dict.get('Sis.Enf.')} /></SimpleItem>
                                    <SimpleItem dataField='CAR_UYG_KOZM' editorType='dxCheckBox'><Label text={Dict.get('Kozm.Kul.')} /></SimpleItem>
                                    <SimpleItem dataField='CAR_UYG_ALN_ENFK' editorType='dxCheckBox'><Label text={Dict.get('Alan.Enf.')} /></SimpleItem>
                                    <SimpleItem dataField='CAR_ILAC_KUL' editorType='dxCheckBox'><Label text={Dict.get('Ilaç Kul.')} /></SimpleItem>
                                    <SimpleItem dataField='CAR_KAN_YATKN' editorType='dxCheckBox'><Label text={Dict.get('Kanama.Yat.')} /></SimpleItem>
                                    <SimpleItem dataField='CAR_ASP_KANSU_KUL' editorType='dxCheckBox'><Label text={Dict.get('Aspr.Kan Su.')} /></SimpleItem>
                                    <SimpleItem dataField='CAR_HBS_HCV' editorType='dxCheckBox'><Label text={Dict.get('HBsAg,HCV')} /></SimpleItem>
                                </GroupItem>
                            </Tab>
                            <Tab title={Dict.get('Dökülme Tipi')}>
                                <GroupItem colCount={2}>
                                    {Array.from({ length: 15 }).map((_, idx) => (
                                        <GroupItem key={idx} colCount={5}>
                                            <SimpleItem colSpan={1} dataField={`DT${String(idx + 1).padStart(2, '0')}`} editorType='dxCheckBox' label={{ visible: false }} />
                                            <SimpleItem colSpan={4}>
                                                <img src={process.env.PUBLIC_URL + `/media/dokulme_tipi/${idx}.png`} alt="" style={{ marginLeft: '10px', height: '60px', width: '60px' }} loading="lazy" />
                                            </SimpleItem>
                                        </GroupItem>
                                    ))}
                                </GroupItem>
                            </Tab>
                        </TabbedItem>
                    </GroupItem>               
                     </Form>
            </ScrollView>
            </div>
        </Popup>
    );
}
